import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import AutoRefreshCountdown from '../common/AutoRefreshCountdown';
import ManualSurplusModal from '../surplus/ManualSurplusModal';
import { addMaterialSurplus, removeMaterialSurplus } from '../surplus/surplusApi';
import { errorMessage } from '../packaging/packingApi';

interface SurplusItem {
  /** Computed from production — or null for surplus an admin added by hand (then manual_id is set). */
  production_item_id: number | null;
  manual_id: number | null;
  indeks_kat: string | null;
  material: string | null;
  kolor: string | null;
  barwnik: string | null;
  linia: string | null;
  tydzien: string | null;
  do_produkcji: number | null;
  wyprodukowano: number | null;
  surplus: number;
}

interface SurplusGroup {
  barwnik: string | null;
  kolor: string | null;
  total_surplus: number;
  items: SurplusItem[];
}

// Shared column widths — every dye group renders its own table, so fixed widths
// keep the columns aligned across groups.
const COLUMN_WIDTHS = ['20%', '22%', '16%', '10%', '10%', '12%', '10%'];
// Admin view: one more column for removing surplus added by hand.
const ADMIN_COLUMN_WIDTHS = ['18%', '20%', '14%', '9%', '9%', '11%', '9%', '10%'];

const groupKey = (group: SurplusGroup): string => `${group.barwnik ?? ''}|${group.kolor ?? ''}`;

interface SurplusResponse {
  total_surplus: number;
  groups: SurplusGroup[];
}

export default function SurplusView() {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const [adding, setAdding] = useState(false);
  const [data, setData] = useState<SurplusResponse>({ total_surplus: 0, groups: [] });
  const [loading, setLoading] = useState(true);
  // Expanded dye groups, keyed by dye + color so the state survives auto-refresh.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleGroup = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const allExpanded = data.groups.length > 0 && data.groups.every((g) => expanded.has(groupKey(g)));

  const toggleAll = () => {
    setExpanded(allExpanded ? new Set() : new Set(data.groups.map(groupKey)));
  };

  const fetchSurplus = useCallback(async () => {
    try {
      const res = await api.get<SurplusResponse>('/warehouse/surplus');
      setData(res.data ?? { total_surplus: 0, groups: [] });
    } catch {
      setData({ total_surplus: 0, groups: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSurplus();
  }, [fetchSurplus]);

  const removeManual = async (item: SurplusItem) => {
    if (item.manual_id === null || !window.confirm(t('manualSurplus.confirmRemove'))) return;
    try {
      await removeMaterialSurplus(item.manual_id);
      await fetchSurplus();
    } catch (e: any) {
      window.alert(errorMessage(e, t('manualSurplus.errorSave')));
    }
  };

  const widths = isAdmin ? ADMIN_COLUMN_WIDTHS : COLUMN_WIDTHS;

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>
            📦 {t('surplus.title')}
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>
            {t('surplus.subtitle')}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              style={{
                background: '#667eea', border: '1px solid #667eea', color: '#fff',
                borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              + {t('manualSurplus.add')}
            </button>
          )}
          <AutoRefreshCountdown intervalSeconds={600} onRefresh={fetchSurplus} />
        </div>
      </div>

      {/* Total */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{
          flex: 1, minWidth: 200, background: '#ecfdf5', border: '1px solid #10b98130',
          borderRadius: 10, padding: '14px 20px',
        }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#10b981' }}>{data.total_surplus}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{t('surplus.totalLabel')}</div>
        </div>
      </div>

      {!loading && data.groups.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button
            type="button"
            onClick={toggleAll}
            style={{
              background: '#fff', border: '1px solid #667eea', color: '#667eea',
              borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {allExpanded ? t('surplus.collapseAll') : t('surplus.expandAll')}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>{t('surplus.loading')}</div>
      ) : data.groups.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 48, background: '#f8fafc',
          borderRadius: 12, border: '1px dashed #e2e8f0', color: '#94a3b8',
        }}>
          {t('surplus.empty')}
        </div>
      ) : (
        data.groups.map((group) => {
          const key = groupKey(group);
          const isExpanded = expanded.has(key);
          return (
          <div key={key} style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
            marginBottom: 12, overflow: 'hidden',
          }}>
            {/* Group header — dye + total surplus; click toggles the item list */}
            <div
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              onClick={() => toggleGroup(key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleGroup(key);
                }
              }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none',
                padding: '12px 18px', borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none',
                borderLeft: '4px solid #667eea', background: '#f8fafc',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <span style={{
                  display: 'inline-block', width: 12, fontSize: 12, color: '#667eea',
                  transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s',
                }}>
                  ▶
                </span>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                  {group.barwnik ?? t('surplus.noDye')}
                </span>
                {group.kolor && (
                  <span style={{ fontSize: 13, color: '#64748b' }}>{group.kolor}</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
                <span style={{ fontSize: 13, color: '#64748b' }}>
                  {t('surplus.itemsCount', { count: group.items.length })}
                </span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#10b981' }}>
                  {t('surplus.groupTotal', { count: group.total_surplus })}
                </span>
              </div>
            </div>

            {/* Items */}
            {isExpanded && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 720, tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 13 }}>
                <colgroup>
                  {widths.map((width, ci) => <col key={ci} style={{ width }} />)}
                </colgroup>
                <thead>
                  <tr style={{ background: '#fff', color: '#64748b', textAlign: 'left' }}>
                    <th style={{ padding: '8px 14px', fontWeight: 600 }}>{t('surplus.colIndex')}</th>
                    <th style={{ padding: '8px 14px', fontWeight: 600 }}>{t('surplus.colMaterial')}</th>
                    <th style={{ padding: '8px 14px', fontWeight: 600 }}>{t('surplus.colLine')}</th>
                    <th style={{ padding: '8px 14px', fontWeight: 600 }}>{t('surplus.colWeek')}</th>
                    <th style={{ padding: '8px 14px', fontWeight: 600, textAlign: 'right' }}>{t('surplus.colPlan')}</th>
                    <th style={{ padding: '8px 14px', fontWeight: 600, textAlign: 'right' }}>{t('surplus.colProduced')}</th>
                    <th style={{ padding: '8px 14px', fontWeight: 600, textAlign: 'right' }}>{t('surplus.colSurplus')}</th>
                    {isAdmin && <th style={{ padding: '8px 14px' }} aria-label={t('manualSurplus.remove')} />}
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((it) => (
                    <tr key={it.manual_id !== null ? `manual-${it.manual_id}` : it.production_item_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 14px', fontWeight: 600, color: '#1e293b' }}>
                        {it.indeks_kat ?? '-'}
                        {it.manual_id !== null && (
                          <span style={{
                            marginLeft: 6, padding: '1px 6px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                            background: '#e0e7ff', color: '#3730a3', whiteSpace: 'nowrap',
                          }}>
                            {t('manualSurplus.badge')}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '8px 14px', color: '#475569' }}>{it.material ?? '-'}</td>
                      <td style={{ padding: '8px 14px', color: '#475569' }}>{it.linia ?? '-'}</td>
                      <td style={{ padding: '8px 14px', color: '#475569' }}>{it.tydzien ?? '-'}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'right', color: '#475569' }}>{it.do_produkcji ?? '-'}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'right', color: '#475569' }}>{it.wyprodukowano ?? '-'}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700, color: '#10b981' }}>+{it.surplus}</td>
                      {isAdmin && (
                        <td style={{ padding: '6px 14px', textAlign: 'right' }}>
                          {it.manual_id !== null && (
                            <button
                              type="button"
                              onClick={() => void removeManual(it)}
                              style={{
                                background: '#fff', border: '1px solid #dc2626', color: '#dc2626',
                                borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              }}
                            >
                              {t('manualSurplus.remove')}
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
          );
        })
      )}

      {adding && (
        <ManualSurplusModal
          title={t('manualSurplus.materialTitle')}
          onClose={() => setAdding(false)}
          onSave={async (index, quantity) => {
            await addMaterialSurplus(index, quantity);
            setAdding(false);
            await fetchSurplus();
          }}
        />
      )}
    </div>
  );
}
